import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { useQueryList } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import {
  GetVolunteeringActivitiesListProps,
  VolunteeringActivity
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { type Err } from './CreateActivityTagModal/types'
import { CreateActivityTagModal } from './CreateActivityTagModal/CreateActivityTagModal'
import { CreateActivityTagSuccessModal } from './CreateActivityTagModal/SuccessModal'
import { CreateActivityTagFailureModal } from './CreateActivityTagModal/FailureModal'
import { EditActivityTagModal } from './EditActivityTagModal/EditActivityTagModal'
import { EditActivityTagSuccessModal } from './EditActivityTagModal/SuccessModal'
import { EditActivityTagFailureModal } from './EditActivityTagModal/FailureModal'
import { ActivityTagActionsMenu } from './ActivityTagActionsMenu/ActivityTagActionsMenu'

const PAGE_SIZE = 10
const initialParams: GetVolunteeringActivitiesListProps = { pageSize: PAGE_SIZE }

export function ActivityTagsTable() {
  const { t } = useTranslation()
  const { workplaceGivingService } = useServices()

  const [direction, setDirection] = useState<TableColumnSortDirection | undefined>(undefined)
  const [sortBy, setSortBy] = useState<GetVolunteeringActivitiesListProps['sort']>(undefined)

  const [isCreating, setIsCreating] = useState(false)
  const [createSuccess, setCreateSuccess] = useState(false)
  const [createError, setCreateError] = useState<Err | null>(null)

  const [editingTag, setEditingTag] = useState<VolunteeringActivity | null>(null)
  const [editSuccess, setEditSuccess] = useState(false)
  const [editError, setEditError] = useState<Err | null>(null)

  const [{ data, isLoading, errorMessage, totalResults }, { nextPage, previousPage, query, refresh }] = useQueryList(
    workplaceGivingService.getVolunteeringActivities,
    initialParams
  )

  const columns = useMemo(
    () => [
      { id: 'name', content: t('table.header.name'), isSortable: true, props: { width: '95%' } },
      { id: '', isSortable: false, props: { width: '5%' } }
    ],
    [t]
  )

  const handleRequestSort = (columnKey: string) => {
    const newDirection: TableColumnSortDirection = direction === 'ASC' ? 'DESC' : 'ASC'
    const newSortBy = columnKey as GetVolunteeringActivitiesListProps['sort']

    setDirection(newDirection)
    setSortBy(newSortBy)
    query({ pageSize: PAGE_SIZE, sort: newSortBy, direction: newDirection })
  }

  const onCreateSuccess = () => {
    setIsCreating(false)
    setCreateSuccess(true)
    refresh()
  }

  const onCreateError = (error?: Err) => {
    setIsCreating(false)
    setCreateError(error ?? null)
  }

  const onEditSuccess = () => {
    setEditingTag(null)
    setEditSuccess(true)
    refresh()
  }

  const onEditError = (error?: Err) => {
    setEditingTag(null)
    setEditError(error ?? null)
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <Flex justify="flex-end" pt="md" px="md">
        <Button onClick={() => setIsCreating(true)} data-testid="create-activity-tag-button">
          {t('button.createActivityTag')}
        </Button>
      </Flex>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="no-results"
        emptyTableTitle={t('typography.noActivityTagsTitle')}
        emptyTableDescription={t('typography.noActivityTagsDescription')}
        sortDirection={direction}
        sortByColumn={sortBy ?? undefined}
        handleRequestSort={handleRequestSort}
      >
        <Table>
          <TableBody>
            {data?.map(tag => (
              <TableRow key={tag.id} data-testid={`activity-tag-row-${tag.id}`}>
                <TableCell>{tag.name}</TableCell>
                <TableCell>
                  <ActivityTagActionsMenu tag={tag} onEdit={setEditingTag} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      <CreateActivityTagModal
        isOpen={isCreating}
        handleClose={() => setIsCreating(false)}
        onSuccess={onCreateSuccess}
        onError={onCreateError}
      />
      <CreateActivityTagSuccessModal isOpen={createSuccess} handleClose={() => setCreateSuccess(false)} />
      <CreateActivityTagFailureModal
        isOpen={!!createError}
        handleClose={() => setCreateError(null)}
        error={createError}
      />
      <EditActivityTagModal
        isOpen={!!editingTag}
        handleClose={() => setEditingTag(null)}
        tag={editingTag}
        onSuccess={onEditSuccess}
        onError={onEditError}
      />
      <EditActivityTagSuccessModal isOpen={editSuccess} handleClose={() => setEditSuccess(false)} />
      <EditActivityTagFailureModal isOpen={!!editError} handleClose={() => setEditError(null)} error={editError} />
    </>
  )
}
